import {
  addDays, addMonths, addWeeks, endOfDay, endOfMonth, endOfWeek, format, isSameDay,
  isSameMonth, isToday, startOfDay, startOfMonth, startOfWeek,
} from "date-fns";
import { CalendarDays, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { EventDialog } from "@/components/calendar/EventDialog";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import {
  expandEvents, useCalendars, useEvents, useUpdateEvent, type Occurrence,
} from "@/lib/calendar";
import { cn } from "@/lib/cn";
import { googleToOccurrences, useGoogleEvents, useGoogleStatus } from "@/lib/google";

type View = "month" | "week" | "agenda";

/** Weeks start Monday here — the header labels and the grid have to agree. */
const WEEK_OPTS = { weekStartsOn: 1 } as const;
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const DOT: Record<string, string> = {
  sand: "bg-tile-sand", coral: "bg-tile-coral", amber: "bg-tile-amber",
  mint: "bg-tile-mint", sky: "bg-tile-sky", lilac: "bg-tile-lilac",
};

export function CalendarPage() {
  const [view, setView] = useState<View>("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [editing, setEditing] = useState<Occurrence | null>(null);
  const [creatingOn, setCreatingOn] = useState<Date | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);

  const { data: calData } = useCalendars();
  const updateEvent = useUpdateEvent();

  // The fetch window is the visible grid, not the calendar month — a month view
  // shows leading and trailing days from the neighbouring months.
  const [from, to] = useMemo(() => {
    if (view === "week") {
      return [startOfWeek(cursor, WEEK_OPTS), endOfWeek(cursor, WEEK_OPTS)] as const;
    }
    if (view === "agenda") {
      return [startOfDay(cursor), endOfDay(addDays(cursor, 30))] as const;
    }
    return [
      startOfWeek(startOfMonth(cursor), WEEK_OPTS),
      endOfWeek(endOfMonth(cursor), WEEK_OPTS),
    ] as const;
  }, [view, cursor]);

  const { data, isPending } = useEvents(from, to);

  // The Google layer is read-only and additive: it never blocks the page, so a
  // slow or failing Google call just means the Sebas events render on their own.
  const { data: googleStatus } = useGoogleStatus();
  const { data: googleData } = useGoogleEvents(from, to, Boolean(googleStatus?.connected));

  const occurrences = useMemo(
    () => [
      ...expandEvents(data?.events ?? [], from, to),
      ...googleToOccurrences(googleData?.events ?? []),
    ].sort((a, b) => a.start.getTime() - b.start.getTime()),
    [data, googleData, from, to],
  );

  const byDay = useMemo(() => {
    const map = new Map<string, Occurrence[]>();
    for (const o of occurrences) {
      const key = format(o.start, "yyyy-MM-dd");
      map.set(key, [...(map.get(key) ?? []), o]);
    }
    return map;
  }, [occurrences]);

  const step = (dir: 1 | -1) =>
    setCursor((d) => (view === "month" ? addMonths(d, dir) : view === "week" ? addWeeks(d, dir) : addDays(d, dir * 7)));

  /** Drag-to-reschedule: shift both ends by the same delta to keep duration. */
  const rescheduleTo = (occ: Occurrence, day: Date) => {
    // Google events are mirrored in read-only; there is nothing of ours to move.
    if (occ.source === "google") return;
    const newStart = new Date(occ.start);
    newStart.setFullYear(day.getFullYear(), day.getMonth(), day.getDate());
    const delta = newStart.getTime() - occ.start.getTime();
    if (delta === 0) return;
    updateEvent.mutate({
      id: occ.id,
      startsAt: new Date(new Date(occ.startsAt).getTime() + delta).toISOString(),
      endsAt: new Date(new Date(occ.endsAt).getTime() + delta).toISOString(),
    });
  };

  const dropOnDay = (day: Date) => {
    const occ = occurrences.find((o) => o.occurrenceKey === dragging);
    if (occ) rescheduleTo(occ, day);
    setDragging(null);
  };

  /** Sebas events open the editor; mirrored ones open where they really live. */
  const openOccurrence = (o: Occurrence) => {
    if (o.source === "google") {
      if (o.externalLink) window.open(o.externalLink, "_blank", "noopener,noreferrer");
      return;
    }
    setEditing(o);
  };

  /** Tapping a day in the month grid drops you into that week — the drill-in. */
  const pickDay = (day: Date) => {
    setCursor(day);
    setView("week");
  };

  const days = useMemo(() => {
    const out: Date[] = [];
    for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
    return out;
  }, [from, to]);

  const grid = { byDay, dragging, setDragging, onDropDay: dropOnDay, onOpen: openOccurrence };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="font-display text-4xl font-semibold sm:text-5xl lg:text-6xl">
          {format(cursor, "MMMM")}
          <br />
          <span className="text-ink-muted">{format(cursor, "yyyy")}</span>
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="icon" onClick={() => step(-1)} aria-label="Previous">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setCursor(new Date())}>
            Today
          </Button>
          <Button variant="secondary" size="icon" onClick={() => step(1)} aria-label="Next">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(["month", "week", "agenda"] as View[]).map((v) => (
          <Chip key={v} active={view === v} onClick={() => setView(v)}>
            {v[0]!.toUpperCase() + v.slice(1)}
          </Chip>
        ))}
        <Button size="sm" className="ml-auto" onClick={() => setCreatingOn(new Date())}>
          <Plus className="h-4 w-4" /> New event
        </Button>
      </div>

      {isPending ? (
        <p className="text-sm text-ink-faint">Loading…</p>
      ) : view === "agenda" ? (
        <AgendaView occurrences={occurrences} onOpen={openOccurrence} />
      ) : view === "week" ? (
        <WeekView days={days} cursor={cursor} onCreate={setCreatingOn} {...grid} />
      ) : (
        <MonthGrid
          days={days}
          cursor={cursor}
          onCreate={setCreatingOn}
          onPickDay={pickDay}
          {...grid}
        />
      )}

      {(editing || creatingOn) && calData?.calendars.length ? (
        <EventDialog
          calendars={calData.calendars}
          occurrence={editing}
          defaultDate={creatingOn ?? undefined}
          onClose={() => {
            setEditing(null);
            setCreatingOn(null);
          }}
        />
      ) : null}
    </div>
  );
}

/* --------------------------------------------------------------- shared bits */

type GridShared = {
  byDay: Map<string, Occurrence[]>;
  dragging: string | null;
  setDragging: (key: string | null) => void;
  onDropDay: (day: Date) => void;
  onOpen: (o: Occurrence) => void;
};

function EventChip({
  o,
  dragging,
  setDragging,
  onOpen,
  size = "compact",
  showTime = true,
}: {
  o: Occurrence;
  dragging: string | null;
  setDragging: (key: string | null) => void;
  onOpen: (o: Occurrence) => void;
  size?: "compact" | "full";
  showTime?: boolean;
}) {
  const full = size === "full";
  return (
    <button
      draggable={o.source !== "google"}
      onDragStart={() => setDragging(o.occurrenceKey)}
      onDragEnd={() => setDragging(null)}
      onClick={(e) => {
        e.stopPropagation();
        onOpen(o);
      }}
      title={o.source === "google" ? `${o.title} — Google Calendar` : o.title}
      className={cn(
        "flex w-full items-center gap-1.5 rounded-lg text-left",
        full ? "px-2.5 py-2 text-sm" : "px-1.5 py-1 text-[0.7rem] leading-tight",
        // Mirrored events read as a quieter, outlined layer so a glance tells you
        // what you can actually edit here.
        o.source === "google"
          ? "border border-dashed border-line bg-transparent text-ink-muted hover:bg-sunken"
          : "bg-sunken text-ink hover:bg-line/60",
        dragging === o.occurrenceKey && "opacity-40",
      )}
    >
      <span className={cn("shrink-0 rounded-full", full ? "h-2 w-2" : "h-1.5 w-1.5", DOT[o.color])} />
      <span className="min-w-0 flex-1 truncate">
        {showTime && !o.allDay ? (
          <span className="tabular-nums text-ink-faint">{format(o.start, "HH:mm")} </span>
        ) : null}
        {o.title}
      </span>
    </button>
  );
}

/* --------------------------------------------------------------------- month */

function MonthGrid({
  days,
  cursor,
  onCreate,
  onPickDay,
  byDay,
  dragging,
  setDragging,
  onDropDay,
  onOpen,
}: GridShared & {
  days: Date[];
  cursor: Date;
  onCreate: (day: Date) => void;
  onPickDay: (day: Date) => void;
}) {
  const MAX = 3;
  return (
    <div className="grid grid-cols-7 gap-px overflow-hidden rounded-card border border-line bg-line">
      {WEEKDAYS.map((d) => (
        <div
          key={d}
          className="bg-surface py-2 text-center text-[0.7rem] font-medium uppercase tracking-wide text-ink-faint"
        >
          {d}
        </div>
      ))}

      {days.map((day) => {
        const key = format(day, "yyyy-MM-dd");
        const evs = byDay.get(key) ?? [];
        const outside = !isSameMonth(day, cursor);
        return (
          <div
            key={key}
            onDoubleClick={() => onCreate(day)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDropDay(day)}
            className={cn(
              "flex min-h-[5.5rem] flex-col gap-1 bg-surface p-1 sm:min-h-28 sm:p-1.5",
              outside && "text-ink-faint",
            )}
          >
            <button
              type="button"
              onClick={() => onPickDay(day)}
              aria-label={`Open ${format(day, "EEEE d MMMM")}`}
              className={cn(
                "grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs transition-colors",
                isToday(day)
                  ? "bg-accent font-semibold text-accent-ink"
                  : outside
                    ? "text-ink-faint hover:bg-sunken"
                    : "text-ink-muted hover:bg-sunken",
              )}
            >
              {format(day, "d")}
            </button>

            <div className="flex min-h-0 flex-col gap-0.5">
              {evs.slice(0, MAX).map((o) => (
                <EventChip
                  key={o.occurrenceKey}
                  o={o}
                  dragging={dragging}
                  setDragging={setDragging}
                  onOpen={onOpen}
                  showTime={false}
                />
              ))}
              {evs.length > MAX ? (
                <button
                  type="button"
                  onClick={() => onPickDay(day)}
                  className="px-1 text-left text-[0.65rem] text-ink-faint hover:text-ink"
                >
                  +{evs.length - MAX} more
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------------- week */

function WeekView({
  days,
  cursor,
  onCreate,
  byDay,
  dragging,
  setDragging,
  onDropDay,
  onOpen,
}: GridShared & {
  days: Date[];
  cursor: Date;
  onCreate: (day: Date) => void;
}) {
  const strip = useRef<HTMLDivElement>(null);
  const focused = useRef<HTMLDivElement>(null);

  // Open on the day you were looking at, without nudging the page up or down.
  useEffect(() => {
    const c = strip.current;
    const t = focused.current;
    if (c && t) c.scrollLeft = t.offsetLeft - 20;
  }, [cursor, days]);

  return (
    <>
      {/* Phone: a snapping strip of wide, readable day columns. */}
      <div
        ref={strip}
        className="-mx-5 flex snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain scroll-pl-5 px-5 pb-2 [scrollbar-width:none] sm:hidden [&::-webkit-scrollbar]:hidden"
      >
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const evs = byDay.get(key) ?? [];
          return (
            <div
              key={key}
              ref={isSameDay(day, cursor) ? focused : undefined}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDropDay(day)}
              onDoubleClick={() => onCreate(day)}
              className={cn(
                "flex w-[78vw] shrink-0 snap-start flex-col gap-2 rounded-2xl border bg-surface p-3",
                isToday(day) ? "border-accent" : "border-line",
              )}
            >
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-medium text-ink-faint">{format(day, "EEE")}</span>
                <span
                  className={cn(
                    "grid h-7 w-7 place-items-center rounded-full text-sm",
                    isToday(day) ? "bg-accent font-semibold text-accent-ink" : "text-ink",
                  )}
                >
                  {format(day, "d")}
                </span>
              </div>
              <div className="flex flex-col gap-1.5">
                {evs.length === 0 ? (
                  <button
                    type="button"
                    onClick={() => onCreate(day)}
                    className="rounded-lg border border-dashed border-line py-6 text-center text-xs text-ink-faint hover:text-ink"
                  >
                    Add event
                  </button>
                ) : (
                  evs.map((o) => (
                    <EventChip
                      key={o.occurrenceKey}
                      o={o}
                      dragging={dragging}
                      setDragging={setDragging}
                      onOpen={onOpen}
                      size="full"
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop: the familiar seven-column week. */}
      <div className="hidden grid-cols-7 gap-px overflow-hidden rounded-card border border-line bg-line sm:grid">
        {days.map((day) => (
          <div
            key={format(day, "yyyy-MM-dd")}
            className="bg-surface py-2 text-center text-[0.7rem] font-medium uppercase tracking-wide text-ink-faint"
          >
            {format(day, "EEE d")}
          </div>
        ))}
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const evs = byDay.get(key) ?? [];
          return (
            <div
              key={key}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDropDay(day)}
              onDoubleClick={() => onCreate(day)}
              className="flex min-h-[16rem] flex-col gap-1 bg-surface p-1.5"
            >
              {evs.map((o) => (
                <EventChip
                  key={o.occurrenceKey}
                  o={o}
                  dragging={dragging}
                  setDragging={setDragging}
                  onOpen={onOpen}
                />
              ))}
            </div>
          );
        })}
      </div>
    </>
  );
}

/* -------------------------------------------------------------------- agenda */

function AgendaView({
  occurrences,
  onOpen,
}: {
  occurrences: Occurrence[];
  onOpen: (o: Occurrence) => void;
}) {
  if (occurrences.length === 0) {
    return (
      <div className="rounded-card border border-dashed border-line px-6 py-14 text-center">
        <CalendarDays className="mx-auto h-6 w-6 text-ink-faint" />
        <p className="font-display mt-3 text-2xl font-semibold">Nothing scheduled</p>
        <p className="mt-1 text-sm text-ink-muted">The next 30 days are clear.</p>
      </div>
    );
  }

  const groups: { day: Date; items: Occurrence[] }[] = [];
  for (const o of occurrences) {
    const last = groups[groups.length - 1];
    if (last && isSameDay(last.day, o.start)) last.items.push(o);
    else groups.push({ day: o.start, items: [o] });
  }

  return (
    <div className="flex flex-col gap-6">
      {groups.map(({ day, items }) => (
        <section key={day.toISOString()} className="flex gap-4">
          <div className="w-14 shrink-0 text-right">
            <p className={cn("font-display text-2xl font-semibold", isToday(day) && "text-tile-coral")}>
              {format(day, "d")}
            </p>
            <p className="text-xs text-ink-faint">{format(day, "EEE")}</p>
          </div>
          <div className="flex flex-1 flex-col gap-2">
            {items.map((o) => (
              <button
                key={o.occurrenceKey}
                onClick={() => onOpen(o)}
                className={cn(
                  "flex items-center gap-3 rounded-2xl border px-4 py-3 text-left",
                  o.source === "google"
                    ? "border-dashed border-line bg-transparent"
                    : "border-line bg-raised",
                )}
              >
                <span className={cn("h-8 w-1 shrink-0 rounded-full", DOT[o.color])} />
                <span className="flex-1">
                  <span className="block text-sm font-medium text-ink">{o.title}</span>
                  <span className="block text-xs text-ink-muted">
                    {o.allDay ? "All day" : `${format(o.start, "HH:mm")} – ${format(o.end, "HH:mm")}`}
                    {o.location ? ` · ${o.location}` : ""}
                    {o.isRecurring ? " · repeats" : ""}
                    {o.source === "google" ? " · Google" : ""}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
